import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridReadyEvent, CellStyle, GridApi } from "ag-grid-community";
import {
  PlxsText,
  PlxsButton,
  PlxsChip,
  PlxsMultiSelect,
  PlxsTextLink,
} from "@plexlogic/plexus-ui";
import apiService from "../../services/api";
import {
  getActionDisplayName,
  getActionChipTone,
  getAllActionOptions,
} from "../../utils/actionRegistry";
import { formatDateTime } from "../../utils/dateTimeUtils";
import WizardRefreshButton from "../WizardRefreshButton";
import "../../styles/ag-grid-plexus-theme.css";
import "./AuditDashboard.css";
import { toSentenceCase } from "../../utils";

interface AuditLog {
  id: string;
  entity_type: string;
  entity_id: string;
  action_type: string;
  action_description: string | null;
  actor_id: string | null;
  actor_email: string | null;
  actor_name: string | null;
  actor_type: string;
  ip_address: string | null;
  metadata: Record<string, any>;
  created_at: string;
  draw_id: string | null;
  comment_text: string | null;
  is_comment: boolean;
}

interface AuditLogsResponse {
  logs: AuditLog[];
  total: number;
  limit: number;
  offset: number;
}

// Action chip cell renderer
const ActionChipRenderer = (params: any) => {
  const actionType = params.value || "";
  const displayName = getActionDisplayName(actionType);
  const tone = getActionChipTone(actionType);

  return (
    <PlxsChip
      dataTestId={`action-chip-${params.data?.id}`}
      label={toSentenceCase(displayName)}
      tone={tone}
    />
  );
};

// Format date/time cell renderer
const DateTimeRenderer = (params: any) => {
  if (!params.value) return "N/A";

  // Use centralized datetime formatting (browser's local timezone)
  const formatted = formatDateTime(params.value);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.125rem",
        lineHeight: "1.3",
      }}
    >
      <span>{formatted.date}</span>
      <span style={{ color: "var(--plexus-color-neutral-300)" }}>
        {formatted.time}
      </span>
    </div>
  );
};

// Custom cell renderer for Draw Name column with clickable link
const DrawNameLinkRenderer = (params: any) => {
  const drawId = params.data?.draw_id;
  const drawName = params.data?.metadata?.draw_name || "N/A";

  // If no draw_id or draw_name is "N/A", show plain text
  if (!drawId || drawName === "N/A") {
    return drawName;
  }

  return (
    <PlxsTextLink
      href={`/draws/${drawId}`}
      target="_blank"
      data-testid={`audit-draw-link-${drawId}`}
    >
      {drawName}
    </PlxsTextLink>
  );
};

const AuditDashboard: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 50;
  const gridApiRef = useRef<GridApi | null>(null);

  // Filter states
  const [selectedActionTypes, setSelectedActionTypes] = useState<string[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string[]>([
    "all_time",
  ]);

  // Action type options for multi-select (from centralized registry)
  const actionTypeOptions = useMemo(() => getAllActionOptions(), []);

  // Time range options for select
  const timeRangeOptions = useMemo(
    () => [
      { label: toSentenceCase("all time"), value: "all_time" },
      { label: toSentenceCase("last 7 days"), value: "last_7_days" },
      { label: toSentenceCase("last 30 days"), value: "last_30_days" },
      { label: toSentenceCase("last 90 days"), value: "last_90_days" },
      { label: toSentenceCase("this year"), value: "this_year" },
    ],
    []
  );

  // Column definitions for AG Grid
  // Fixed Order: Draw → Action → Description → User → Timestamp
  const columnDefs = useMemo<ColDef[]>(() => {
    const ellipsisStyle: CellStyle = {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    };

    const descriptionStyle: CellStyle = {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      lineHeight: "60px",
    };

    const rightAlignStyle: CellStyle = {
      textAlign: "right",
    };

    return [
      {
        headerName: toSentenceCase("draw"),
        field: "metadata.draw_name",
        width: 180,
        minWidth: 140,
        maxWidth: 250,
        sortable: true,
        resizable: true,
        cellStyle: ellipsisStyle,
        tooltipField: "metadata.draw_name",
        cellRenderer: DrawNameLinkRenderer,
        valueGetter: (params) => {
          return params.data?.metadata?.draw_name || "N/A";
        },
      },
      {
        headerName: toSentenceCase("action"),
        field: "action_type",
        width: 180,
        minWidth: 160,
        maxWidth: 220,
        cellRenderer: ActionChipRenderer,
        sortable: true,
        resizable: true,
      },
      {
        headerName: toSentenceCase("description"),
        field: "action_description",
        flex: 2,
        minWidth: 300,
        sortable: false,
        resizable: true,
        wrapText: false,
        autoHeight: false,
        cellStyle: descriptionStyle,
        tooltipValueGetter: (params) => {
          const data = params.data;
          if (data?.is_comment && data?.comment_text) {
            return data.comment_text;
          }
          return data?.action_description || "No description available";
        },
        valueGetter: (params) => {
          const data = params.data;
          if (data?.is_comment && data?.comment_text) {
            return data.comment_text;
          }
          return data?.action_description || "No description available";
        },
      },
      {
        headerName: toSentenceCase("user"),
        field: "actor_email",
        width: 180,
        minWidth: 150,
        maxWidth: 250,
        sortable: true,
        resizable: true,
        cellStyle: ellipsisStyle,
        tooltipValueGetter: (params) => {
          const data = params.data;
          if (data?.actor_name) return data.actor_name;
          if (data?.actor_email) return data.actor_email;
          if (data?.actor_type === "system") return "System";
          return "Unknown";
        },
        valueGetter: (params) => {
          const data = params.data;
          if (data?.actor_name) return data.actor_name;
          if (data?.actor_email) return data.actor_email;
          if (data?.actor_type === "system") return "System";
          return "Unknown";
        },
      },
      {
        headerName: toSentenceCase("timestamp"),
        field: "created_at",
        width: 180,
        minWidth: 160,
        maxWidth: 200,
        cellRenderer: DateTimeRenderer,
        sortable: true,
        sort: "desc",
        resizable: true,
        cellStyle: rightAlignStyle,
        headerClass: "ag-right-aligned-header",
      },
    ];
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: false,
    }),
    []
  );

  // Helper function to calculate date range
  const calculateDateRange = (
    timeRange: string
  ): { start_date?: string; end_date?: string } => {
    const now = new Date();
    const startDate = new Date();

    switch (timeRange) {
      case "last_7_days":
        startDate.setDate(now.getDate() - 7);
        break;
      case "last_30_days":
        startDate.setDate(now.getDate() - 30);
        break;
      case "last_90_days":
        startDate.setDate(now.getDate() - 90);
        break;
      case "this_year":
        startDate.setMonth(0, 1);
        break;
      case "all_time":
      default:
        return {};
    }

    return {
      start_date: startDate.toISOString(),
      end_date: now.toISOString(),
    };
  };

  const loadAuditLogs = useCallback(
    async (offset: number = 0) => {
      try {
        setLoading(true);
        setError(null);

        // Use first selected time range or default to 'all_time'
        const timeRange =
          selectedTimeRange.length > 0 ? selectedTimeRange[0] : "all_time";
        const dateRange = calculateDateRange(timeRange);

        const response: AuditLogsResponse = await apiService.getAuditLogs({
          limit: pageSize,
          offset: offset,
          ...(selectedActionTypes.length > 0 && {
            action_type: selectedActionTypes.join(","),
          }),
          ...dateRange,
        });

        setAuditLogs(response.logs);
        setTotalRecords(response.total);
      } catch (err) {
        setError("Failed to load audit logs");
        // Error logging handled by error boundary
      } finally {
        setLoading(false);
      }
    },
    [selectedActionTypes, selectedTimeRange]
  );

  useEffect(() => {
    loadAuditLogs(currentPage * pageSize);
  }, [currentPage, loadAuditLogs]);

  const handleNextPage = () => {
    if ((currentPage + 1) * pageSize < totalRecords) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleRefresh = () => {
    loadAuditLogs(currentPage * pageSize);
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    // Show loading overlay on initial load
    if (loading) {
      params.api.showLoadingOverlay();
    }
  };

  // Update grid overlay when loading state changes
  useEffect(() => {
    if (gridApiRef.current) {
      if (loading) {
        gridApiRef.current.showLoadingOverlay();
      } else if (auditLogs.length === 0) {
        gridApiRef.current.showNoRowsOverlay();
      } else {
        gridApiRef.current.hideOverlay();
      }
    }
  }, [loading, auditLogs]);

  if (error) {
    return (
      <div className="audit-dashboard">
        <div className="audit-dashboard-header">
          <h1>{toSentenceCase("My Activity")}</h1>
        </div>
        <div className="error-message">
          <PlxsText>{error}</PlxsText>
          <WizardRefreshButton
            onClick={handleRefresh}
            label={toSentenceCase("retry")}
            iconOnly
            dataTestId="draw-audit-refresh-button"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="audit-dashboard" data-testid="draw-audit-page">
      {/* Header */}
      <div className="audit-dashboard-header">
        <div className="header-content">
          <h1>{toSentenceCase("My Activity")}</h1>
        </div>
        <div className="header-actions">
          <WizardRefreshButton onClick={handleRefresh} isDisabled={loading} iconOnly/>
        </div>
      </div>

      {/* Filter Section */}
      <div className="audit-filters">
        <div className="filter-controls">
          <div className="filter-item">
            <div
              style={{
                marginBottom: "var(--plexus-space-xs)",
                fontSize: "14px",
                fontWeight: 500,
                color: "var(--plexus-color-neutral-200)",
              }}
            >
              {toSentenceCase("action type")}
            </div>
            <PlxsMultiSelect
              dataTestId="draw-audit-action-type-filter"
              getLabel={(option: { label: string; value: string }) =>
                option.label
              }
              onChange={(selected: { label: string; value: string }[]) => {
                setSelectedActionTypes(selected.map((opt) => opt.value));
                setCurrentPage(0);
              }}
              options={actionTypeOptions}
              placeholder="Select action types"
              value={actionTypeOptions.filter((opt) =>
                selectedActionTypes.includes(opt.value)
              )}
            />
          </div>
          <div className="filter-item">
            <div
              style={{
                marginBottom: "var(--plexus-space-xs)",
                fontSize: "14px",
                fontWeight: 500,
                color: "var(--plexus-color-neutral-200)",
              }}
            >
              {toSentenceCase("time range")}
            </div>
            <PlxsMultiSelect
              dataTestId="draw-audit-time-range-filter"
              getLabel={(option: { label: string; value: string }) =>
                option.label
              }
              onChange={(selected: { label: string; value: string }[]) => {
                // Only allow single selection - keep last selected
                setSelectedTimeRange(
                  selected.length > 0
                    ? [selected[selected.length - 1].value]
                    : ["all_time"]
                );
                setCurrentPage(0);
              }}
              options={timeRangeOptions}
              placeholder="Select time range"
              value={timeRangeOptions.filter((opt) =>
                selectedTimeRange.includes(opt.value)
              )}
            />
          </div>
          <div className="filter-actions">
            <PlxsButton
              dataTestId="draw-audit-clear-filters-button"
              label={toSentenceCase("clear filters")}
              onClick={() => {
                setSelectedActionTypes([]);
                setSelectedTimeRange(["all_time"]);
                setCurrentPage(0);
              }}
              variant="secondary"
              tone="neutral"
              isDisabled={
                selectedActionTypes.length === 0 &&
                selectedTimeRange.length === 1 &&
                selectedTimeRange[0] === "all_time"
              }
            />
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="audit-stats">
        <div className="stat-card">
          <div className="stat-label">{toSentenceCase("total records")}</div>
          <div className="stat-value">{totalRecords.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{toSentenceCase("records per page")}</div>
          <div className="stat-value">{pageSize}</div>
        </div>
      </div>

      {/* AG Grid Table */}
      {/* 
        Note: AG Grid v28 with React 16 compatibility notes:
        1. The 'aria-description' warning is a known issue in ag-grid v28 when used with React 16.
           This is because React 16 doesn't recognize aria-description as a valid ARIA attribute.
           This warning can be safely ignored or resolved by upgrading to React 17+ or ag-grid v29+.
        2. 'enableCellTextSelection' is set at the grid level (not column level) to enable text selection in all cells.
      */}
      <div className="audit-grid-container">
        <div
          className="ag-theme-plexus audit-grid-wrapper"
          style={
            {
              "--ag-row-hover-color": "var(--plexus-color-brand-700)",
            } as React.CSSProperties
          }
        >
          <AgGridReact
            rowData={auditLogs}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={false}
            suppressCellFocus={true}
            enableCellTextSelection={true}
            animateRows={true}
            rowHeight={60}
            headerHeight={48}
            suppressHorizontalScroll={false}
            enableBrowserTooltips={false}
            tooltipShowDelay={500}
            alwaysShowHorizontalScroll={true}
            alwaysShowVerticalScroll={true}
            overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading audit logs...</span>'
            overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No audit logs found</span>'
            suppressMenuHide={true}
          />
        </div>

        {/* Custom Pagination */}
        <div className="custom-pagination">
          <PlxsButton
            dataTestId="draw-audit-previous-page-button"
            label={toSentenceCase("previous")}
            onClick={handlePrevPage}
            variant="secondary"
            tone="neutral"
            isDisabled={currentPage === 0 || loading}
          />
          <PlxsText>
            Showing {currentPage * pageSize + 1} -{" "}
            {Math.min((currentPage + 1) * pageSize, totalRecords)} of{" "}
            {totalRecords}
          </PlxsText>
          <PlxsButton
            dataTestId="draw-audit-next-page-button"
            label={toSentenceCase("next")}
            onClick={handleNextPage}
            variant="secondary"
            tone="neutral"
            isDisabled={(currentPage + 1) * pageSize >= totalRecords || loading}
          />
        </div>
      </div>
    </div>
  );
};

export default AuditDashboard;
